"use client";

import type { ReportData } from "./reportData";

interface AvatarBlueprintSectionProps {
  report: ReportData;
}

export default function AvatarBlueprintSection({ report }: AvatarBlueprintSectionProps) {
  const { blueprint } = report;

  return (
    <section className="fade-in-up">
      {/* Section Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="section-number">2</div>
        <div>
          <h2
            className="text-xl md:text-2xl font-display text-white/95"
            style={{ fontWeight: 700 }}
          >
            Avatar Blueprint
          </h2>
          <p className="text-sm text-white/45 mt-0.5" style={{ fontWeight: 400 }}>
            Personality Analysis · Life Path · Cosmic Influences · Related Archetypes
          </p>
        </div>
      </div>

      {/* Personality Analysis */}
      <div className="nautilus-card p-6 md:p-8 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-1 h-5 rounded-full"
            style={{ background: "linear-gradient(180deg, #dbb84d, rgba(219,184,77,0.3))" }}
          />
          <h3
            className="text-base font-display text-white/90"
            style={{ fontWeight: 600 }}
          >
            Personality Analysis
          </h3>
        </div>
        <p className="text-sm md:text-base text-white/70 leading-relaxed" style={{ fontWeight: 400 }}>
          {blueprint.personalityAnalysis}
        </p>
      </div>

      {/* Life Path Insights */}
      <div className="nautilus-card p-6 md:p-8 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-1 h-5 rounded-full"
            style={{ background: "linear-gradient(180deg, #dbb84d, rgba(219,184,77,0.3))" }}
          />
          <h3
            className="text-base font-display text-white/90"
            style={{ fontWeight: 600 }}
          >
            Life Path Insights
          </h3>
        </div>
        <p className="text-sm md:text-base text-white/70 leading-relaxed" style={{ fontWeight: 400 }}>
          {blueprint.lifePathInsights}
        </p>
      </div>

      {/* Cosmic Influences */}
      <div
        className="rounded-2xl p-6 md:p-8 mb-5 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(0,18,88,0.5) 0%, rgba(54,23,64,0.6) 100%)",
          border: "1px solid rgba(219,184,77,0.2)",
        }}
      >
        {/* Background star decoration */}
        <div
          className="absolute top-4 right-6 text-6xl opacity-5 pointer-events-none select-none"
          aria-hidden="true"
        >
          ✦
        </div>
        <div
          className="absolute bottom-4 left-6 text-4xl opacity-5 pointer-events-none select-none"
          aria-hidden="true"
        >
          ✧
        </div>

        <div className="flex items-center gap-2 mb-4 relative">
          <div
            className="w-1 h-5 rounded-full"
            style={{ background: "linear-gradient(180deg, #dbb84d, rgba(219,184,77,0.3))" }}
          />
          <h3
            className="text-base font-display text-white/90"
            style={{ fontWeight: 600 }}
          >
            Cosmic Influences
          </h3>
        </div>
        <p className="text-sm md:text-base text-white/70 leading-relaxed relative" style={{ fontWeight: 400 }}>
          {blueprint.cosmicInfluences}
        </p>
      </div>

      {/* Related Archetypes */}
      <div className="nautilus-card p-6 md:p-8">
        <div className="flex items-center gap-2 mb-6">
          <div
            className="w-1 h-5 rounded-full"
            style={{ background: "linear-gradient(180deg, #dbb84d, rgba(219,184,77,0.3))" }}
          />
          <h3
            className="text-base font-display text-white/90"
            style={{ fontWeight: 600 }}
          >
            Related Archetypes
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {blueprint.relatedArchetypes.map((archetype, i) => (
            <div
              key={i}
              className="rounded-xl p-5 relative overflow-hidden group transition-all duration-200 hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg, rgba(124,62,97,0.2) 0%, rgba(0,18,88,0.3) 100%)",
                border: "1px solid rgba(219,184,77,0.2)",
              }}
            >
              {/* Number badge */}
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center mb-3"
                style={{
                  background: "linear-gradient(135deg, #7c3e61, #dbb84d)",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  color: "#0a0614",
                }}
              >
                {i + 1}
              </div>
              <h4
                className="text-sm font-display gold-text mb-2 leading-tight"
                style={{ fontWeight: 700 }}
              >
                {archetype.name}
              </h4>
              <p className="text-xs text-white/60 leading-relaxed" style={{ fontWeight: 400 }}>
                {archetype.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}